import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { NS, PROFILE_KEYS } from '../../../i18n/constants/keys';
import { hasStoredProfilePicture } from '../functions';

interface IProfileHeaderProps {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  profileLink: string;
  profilePicStatus: string;
  userInitials: string;
  isEditing: boolean;
  loading: boolean;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onOpenPictureModal: () => void;
}

/**
 * Brand-reactive profile banner (primary → secondary gradient follows the
 * organization theme, same tokens as `/mui-preview`). Stacks vertically with
 * full-width actions on xs, horizontal on sm+.
 */
export const ProfileHeader = ({
  firstName,
  lastName,
  email,
  jobTitle,
  profileLink,
  profilePicStatus,
  userInitials,
  isEditing,
  loading,
  onStartEdit,
  onSave,
  onCancel,
  onOpenPictureModal,
}: IProfileHeaderProps) => {
  const { t } = useTranslation(NS.profile);
  const hasPicture = hasStoredProfilePicture(profileLink);

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2.5, sm: 3 },
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: 'primary.contrastText',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 2, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box sx={{ position: 'relative', '&:hover .pic-edit': { opacity: 1 } }}>
          <Avatar
            src={hasPicture ? profileLink : undefined}
            alt={t(PROFILE_KEYS.page.profileAlt)}
            sx={{
              width: 96,
              height: 96,
              fontSize: '1.75rem',
              fontWeight: 700,
              bgcolor: 'rgba(255,255,255,0.2)',
              border: '4px solid rgba(255,255,255,0.3)',
            }}
          >
            {!hasPicture && userInitials}
          </Avatar>
          <IconButton
            className="pic-edit"
            aria-label={t(PROFILE_KEYS.page.profileAlt)}
            onClick={onOpenPictureModal}
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: 'rgba(0,0,0,0.35)',
              color: '#fff',
              opacity: { xs: 1, sm: 0 },
              transition: 'opacity 200ms ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
            }}
          >
            <PhotoCameraIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h2" sx={{ color: 'inherit', mb: 0.5 }}>
            {firstName} {lastName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {email}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {jobTitle || t(PROFILE_KEYS.fields.jobTitleFallback)}
          </Typography>
          {profilePicStatus === 'Pending' && (
            <Chip
              label={t(PROFILE_KEYS.picture.pending)}
              size="small"
              sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
            />
          )}
          {profilePicStatus === 'Not Approved' && (
            <Chip label={t(PROFILE_KEYS.picture.rejected)} size="small" color="warning" sx={{ mt: 1, fontWeight: 600 }} />
          )}
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {!isEditing ? (
            <Button
              variant="outlined"
              startIcon={<EditIcon fontSize="small" />}
              onClick={onStartEdit}
              fullWidth
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
            >
              {t(PROFILE_KEYS.actions.editProfile)}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={loading ? undefined : <CheckIcon fontSize="small" />}
                onClick={onSave}
                disabled={loading}
                fullWidth
                sx={{ bgcolor: '#fff', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : null}
                {t(PROFILE_KEYS.actions.save)}
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
                fullWidth
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                {t(PROFILE_KEYS.actions.cancel)}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
